import { Component, Input, OnInit, OnDestroy, ViewChild, ElementRef, HostListener, ChangeDetectionStrategy } from "@angular/core";
import { BaseComponent, UserService, EventsService } from "@mypxplat/xplat/core";
import { AvoService } from "@mypxplat/xplat/web/core";
import { filter, take, takeUntil } from "rxjs/operators";

/** Per-state sign-up config (subset the form needs; list_id/source stay server-side). */
interface SignupStateConfig {
  headline?: string;
  /** Label on the CTA that OPENS the form (countdown button / player tab). */
  button_text?: string;
  /** Label on the form's SUBMIT button. */
  submit_button_text?: string;
  form_message?: string;
  optin_message?: string;
  /** When true, the viewer must tick the marketing opt-in box before they can submit. */
  optin_required?: boolean;
  /** Error shown when `optin_required` and they submit without ticking the box. */
  optin_required_message?: string;
  success_message?: string;
  success_header?: string;
  /** Legal disclaimer HTML shown under the form; falls back to the localized default when blank. */
  legal_disclaimer?: string;
}

/**
 * Email-capture sign-up form for the livestream embed (DENG-892/893/930). Rendered in two places,
 * each passing its per-state `config` (countdown vs livestream): the player's Sign-up tab and the
 * countdown's sign-up modal.
 *
 * Collects email + country + marketing opt-in and posts to the public `event/livestream-signup`
 * endpoint, which upserts the contact in Iterable and subscribes them to the per-state list (list +
 * source are resolved server-side from the event's signup_config). On success the configured
 * per-state success message is shown. Fires a Segment cta-clicked on submit. Email prefills for a
 * signed-in viewer.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "fui-embed-signup",
  templateUrl: "fui-embed-signup.component.html",
  standalone: false,
})
export class FuiEmbedSignupComponent extends BaseComponent implements OnInit, OnDestroy {
  /** Per-state admin config: button_text / form_message / success_message. */
  @Input() config: SignupStateConfig = {};
  /** Event this sign-up belongs to (sent to the capture endpoint). */
  @Input() eventId: string = "";
  /** Which form state this is — "countdown" or "livestream" — selects the server-side list/source. */
  @Input() state: string = "countdown";

  /**
   * Shorter display names for a couple of countries whose official API names are unwieldy. Applied
   * for display AND for what we send to Iterable (the payload uses the resolved title) — NOT in the
   * backend endpoint, which stores whatever string we pass.
   */
  private static readonly COUNTRY_NAME_OVERRIDES: Record<string, string> = {
    US: "United States",
    GB: "United Kingdom",
  };
  /** Country-code overrides (front-end only), e.g. the UK's ISO "GB" is sent to Iterable as "UK". */
  private static readonly COUNTRY_CODE_OVERRIDES: Record<string, string> = {
    GB: "UK",
  };

  public email: string = "";
  public countryCode: string = "";
  public marketingOptIn: boolean = false; // default off (explicit opt-in)
  public submitted: boolean = false;
  public submitting: boolean = false;
  public error: string = "";
  public countries: Array<{ title: string; code: string }> = [];

  // --- Country combobox (type-to-filter, keyboard-navigable) ---
  /** Text in the country input: the filter query while typing, the selected title once chosen. */
  public countryQuery: string = "";
  /** Countries matching `countryQuery` (the full list when the query is empty). */
  public filteredCountries: Array<{ title: string; code: string }> = [];
  public countryDropdownOpen: boolean = false;
  /** Index into `filteredCountries` of the keyboard-highlighted option, or -1. */
  public highlightedIndex: number = -1;
  /** Inline position for the dropdown — `position: fixed` so it escapes the modal's overflow/bounds. */
  public dropdownStyle: Record<string, string> = {};
  /** Deferred close so an option click lands before blur hides the list. */
  private _blurTimer: any = null;

  @ViewChild("countryInput") private countryInputRef?: ElementRef<HTMLInputElement>;

  constructor(
    userService: UserService,
    private eventsService: EventsService,
    private avoService: AvoService
  ) {
    super(userService);
  }

  ngOnInit() {
    // Prefill email from the signed-in account (embed viewers are often guests, so best-effort).
    this._prefillEmail(this.user);
    this.userService.user$.pipe(takeUntil(this.destroy$)).subscribe((user) => this._prefillEmail(user));
    // Country list (listCountries returns a { code: name } map; cached + warmed on page load).
    this.userService
      .listCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        this.countries = Object.keys(result || {})
          .filter((code) => !!code) // drop the API's "" → "Select Country" placeholder row
          .map((code) => ({
            title: FuiEmbedSignupComponent.COUNTRY_NAME_OVERRIDES[code] || result[code],
            code: FuiEmbedSignupComponent.COUNTRY_CODE_OVERRIDES[code] || code,
          }));
        this.filteredCountries = this.countries;
      });
    if (typeof document !== "undefined") document.addEventListener("scroll", this._onDocScroll, true);
  }

  ngOnDestroy() {
    this._cancelDeferredClose();
    if (typeof document !== "undefined") document.removeEventListener("scroll", this._onDocScroll, true);
    super.ngOnDestroy();
  }

  private _prefillEmail(user: any) {
    if (!this.submitted && !this.email && user && user.email) this.email = user.email;
  }

  // --- Country combobox handlers ---

  openCountryDropdown() {
    // Once a country is chosen the field is locked (read-only) — it's reopened via the ✕ clear button.
    if (this.countryCode) return;
    this._cancelDeferredClose();
    this.countryDropdownOpen = true;
    this._filterCountries();
    this._positionDropdown();
  }

  onCountryInput(event: Event) {
    this.countryQuery = (event.target as HTMLInputElement).value;
    this.countryCode = ""; // retyping clears any prior selection until they pick again
    this.countryDropdownOpen = true;
    this._filterCountries();
    this.highlightedIndex = this.filteredCountries.length ? 0 : -1;
    this._positionDropdown();
  }

  selectCountry(country: { title: string; code: string }) {
    this._cancelDeferredClose();
    this.countryCode = country.code;
    this.countryQuery = country.title;
    this.countryDropdownOpen = false;
    this.highlightedIndex = -1;
  }

  /** ✕ button: drop the selection, clear the field, and reopen the list to pick again. */
  clearCountry() {
    this.countryCode = "";
    this.countryQuery = "";
    this.highlightedIndex = -1;
    this.countryDropdownOpen = true;
    this._filterCountries();
    this._positionDropdown();
    setTimeout(() => this.countryInputRef?.nativeElement?.focus());
  }

  onCountryKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.countryDropdownOpen) this.openCountryDropdown();
        if (this.filteredCountries.length) {
          this.highlightedIndex = Math.min(this.highlightedIndex + 1, this.filteredCountries.length - 1);
          this._scrollHighlightedIntoView();
        }
        break;
      case "ArrowUp":
        event.preventDefault();
        if (this.filteredCountries.length) {
          this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
          this._scrollHighlightedIntoView();
        }
        break;
      case "Enter":
        // While the list is open, Enter picks the highlight rather than submitting the form.
        if (this.countryDropdownOpen) {
          event.preventDefault();
          if (this.highlightedIndex >= 0 && this.filteredCountries[this.highlightedIndex]) {
            this.selectCountry(this.filteredCountries[this.highlightedIndex]);
          }
        }
        break;
      case "Escape":
        if (this.countryDropdownOpen) {
          event.preventDefault();
          event.stopPropagation();
          this.countryDropdownOpen = false;
        }
        break;
    }
  }

  /** Close on blur (click-outside / tab-away), but deferred so an option click registers first. */
  onCountryBlur() {
    this._blurTimer = setTimeout(() => {
      this.countryDropdownOpen = false;
      this._restoreQueryToSelection();
    }, 150);
  }

  private _cancelDeferredClose() {
    if (this._blurTimer) {
      clearTimeout(this._blurTimer);
      this._blurTimer = null;
    }
  }

  private _filterCountries() {
    const q = this.countryQuery.trim().toLowerCase();
    this.filteredCountries = q ? this.countries.filter((c) => c.title.toLowerCase().includes(q)) : this.countries;
  }

  /** If the viewer typed but didn't pick a match, snap the text back to the selected country (or clear). */
  private _restoreQueryToSelection() {
    const selected = this.countries.find((c) => c.code === this.countryCode);
    this.countryQuery = selected ? selected.title : "";
  }

  private _scrollHighlightedIntoView() {
    setTimeout(() => {
      const el = typeof document !== "undefined" ? document.getElementById(`embed-country-opt-${this.highlightedIndex}`) : null;
      if (el) el.scrollIntoView({ block: "nearest" });
    });
  }

  /**
   * Anchor the dropdown to the input with `position: fixed` so it floats over (and past) the modal
   * instead of being clipped by the panel's overflow.
   *
   * `position: fixed` is normally viewport-anchored, but an ancestor with transform / perspective /
   * filter / backdrop-filter / will-change / contain becomes the containing block for fixed
   * descendants. The floating ("overlay") comments panel sets `backdrop-filter` (and is offset to
   * top/right), so without compensation the dropdown's viewport coords get re-based against the
   * panel and land off-screen — it reads as "not opening". We find that containing-block ancestor
   * and subtract its padding-box origin so the viewport coords resolve correctly. In docked mode
   * there's no such ancestor, origin is (0,0), and this is a no-op.
   */
  private _positionDropdown() {
    const input = this.countryInputRef?.nativeElement;
    if (!input || typeof input.getBoundingClientRect !== "function") return;
    const r = input.getBoundingClientRect();
    let originLeft = 0;
    let originTop = 0;
    const cb = this._fixedContainingBlock(input);
    if (cb && typeof getComputedStyle === "function") {
      const cbr = cb.getBoundingClientRect();
      const cs = getComputedStyle(cb);
      originLeft = cbr.left + (parseFloat(cs.borderLeftWidth) || 0);
      originTop = cbr.top + (parseFloat(cs.borderTopWidth) || 0);
    }
    this.dropdownStyle = {
      position: "fixed",
      top: `${r.bottom + 4 - originTop}px`,
      left: `${r.left - originLeft}px`,
      width: `${r.width}px`,
      right: "auto",
    };
  }

  /**
   * Nearest ancestor that acts as the containing block for `position: fixed` descendants, or null
   * when the dropdown stays viewport-anchored. Mirrors the CSS rules: transform / perspective /
   * filter / backdrop-filter other than `none`, a `will-change` that names one of those, or a
   * `contain` that establishes a paint/layout context.
   */
  private _fixedContainingBlock(el: HTMLElement): HTMLElement | null {
    if (typeof getComputedStyle !== "function") return null;
    let node = el.parentElement;
    while (node) {
      const cs = getComputedStyle(node);
      const webkitBackdrop = (cs as any).webkitBackdropFilter as string | undefined;
      if (
        (cs.transform && cs.transform !== "none") ||
        (cs.perspective && cs.perspective !== "none") ||
        (cs.filter && cs.filter !== "none") ||
        (cs.backdropFilter && cs.backdropFilter !== "none") ||
        (webkitBackdrop && webkitBackdrop !== "none") ||
        (cs.willChange && /transform|perspective|filter/.test(cs.willChange)) ||
        (cs.contain && /\b(layout|paint|strict|content)\b/.test(cs.contain))
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  // Keep the fixed dropdown glued to the input if the viewport resizes while it's open.
  @HostListener("window:resize")
  onViewportResize() {
    if (this.countryDropdownOpen) this._positionDropdown();
  }

  /** Capture-phase scroll handler (bound) so inner-scroll containers (the modal panel) are caught too. */
  private _onDocScroll = () => {
    if (this.countryDropdownOpen) this._positionDropdown();
  };

  submit(event?: Event) {
    if (event) event.preventDefault();
    this.error = "";
    if (!this.email || !this.email.includes("@")) {
      this.error = "Please enter a valid email address.";
      return;
    }
    if (!this.countryCode) {
      this.error = "Please select your country.";
      return;
    }
    if (this.config?.optin_required && !this.marketingOptIn) {
      this.error = this.config.optin_required_message || "Please check the box to continue.";
      return;
    }
    if (this.submitting) return;

    this.trackCtaClicked();
    this.submitting = true;
    const country = this.countries.find((c) => c.code === this.countryCode)?.title || "";
    this.eventsService
      .signupToLivestream({
        event_id: this.eventId,
        state: this.state,
        email: this.email,
        country,
        countryCode: this.countryCode,
        marketingOptIn: this.marketingOptIn,
      })
      .subscribe({
        next: () => {
          this.submitting = false;
          this.submitted = true;
        },
        error: () => {
          this.submitting = false;
          this.error = "Something went wrong. Please try again.";
        },
      });
  }

  /**
   * Segment "cta-clicked" on the sign-up button (house pattern — see product-detail.fireSamplePlayerCta).
   * Fender only; gated on avoInitted$.
   */
  private trackCtaClicked() {
    if (this.userService.appId !== "fender") return;
    const te = this.avoService.trackEvent();
    if (!te || typeof (te as any).ctaClicked !== "function") return;
    const ctaLocation = this.state === "livestream" ? "Livestream Player" : "Livestream Countdown";
    this.avoService.avoInitted$
      .pipe(
        filter((v) => !!v),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const page = this.avoService.getPageProperties({ pageName: "Livestream Embed" });
        const extra: Record<string, any> = {
          ctaText: this.config?.submit_button_text || "Sign Up",
          ctaLocation,
          utmId: (page as any).utmId ?? 0,
          utmTerm: (page as any).utmTerm ?? "",
        };
        const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
        (te as any).ctaClicked(payload);
      });
  }
}
